import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'motion/react';
import { Moon, Sun, ChevronDown, Settings as SettingsIcon, MessageSquare, Info, LogOut, RefreshCw } from 'lucide-react';
import { usePortfolio } from '../context/PortfolioContext';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../context/ThemeContext';
import LanguageSwitcher from './LanguageSwitcher';
import CexAccountSwitcher from './CexAccountSwitcher';
import AddCexAccountModal from './AddCexAccountModal';
import AddWalletModal from './AddWalletModal';
import { SettingsModal } from './SettingsModal';
import { CexAccount } from '../constants';
import { useCexAccount } from '../context/CexAccountContext';
import { useSidebar } from '../context/SidebarContext';
import { useAppKitAccount } from '@reown/appkit/react';
import { useChainId } from 'wagmi';

interface MobileBottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
}

const MobileBottomSheet: React.FC<MobileBottomSheetProps> = ({ isOpen, onClose, children }) => {
  if (typeof document === 'undefined') return null;

  return ReactDOM.createPortal(
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm"
            style={{ zIndex: 9998, pointerEvents: 'auto' }}
            onClick={onClose}
          />
          {/* Bottom Sheet */}
          <motion.div
            initial={{ opacity: 0, y: '100%' }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: '100%' }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed bottom-0 left-0 right-0 bg-[#0D0D0D] border-t border-neutral-800 rounded-t-3xl p-2 pb-10"
            style={{ zIndex: 9999, pointerEvents: 'auto' }}
            data-bottom-sheet
          >
            {children}
          </motion.div>
        </>
      )}
    </AnimatePresence>,
    document.body
  );
};

interface TopBarProps {
  title?: string;
  name?: string;
  avatarInitials?: string;
  children?: React.ReactNode;
  selectedAccount?: CexAccount | null;
  onAccountChange?: (account: CexAccount) => void;
  refreshKey?: number;
  showAccountSwitcher?: boolean;
  showDemoToggle?: boolean;
  isDemoMode?: boolean;
  onDemoToggle?: (isDemo: boolean) => void;
  showResync?: boolean;
  isSyncing?: boolean;
  onResync?: () => void;
}

const TopBar: React.FC<TopBarProps> = ({
  title,
  name,
  avatarInitials,
  children,
  selectedAccount,
  onAccountChange,
  refreshKey: externalRefreshKey,
  showAccountSwitcher = false,
  showDemoToggle = false,
  isDemoMode = false,
  onDemoToggle,
  showResync = false,
  isSyncing = false,
  onResync
}) => {
  const navigate = useNavigate();
  const { userName, logout } = usePortfolio();
  const { t } = useTranslation();
  const { theme, toggleTheme } = useTheme();
  const cexContext = useCexAccount();
  const { isMobileOpen } = useSidebar();
  
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isAddWalletOpen, setIsAddWalletOpen] = useState(false);
  const [addWalletFromWC, setAddWalletFromWC] = useState(false);

  // Reown WalletConnect state
  const { address: wcAddress, isConnected: wcConnected } = useAppKitAccount();
  const [wcAddressForModal, setWcAddressForModal] = useState<string | undefined>(undefined);

  // When WC connects after the user clicked "Connect Wallet" from our modal
  // (which closed our modal first), reopen it with address pre-filled.
  useEffect(() => {
    if (!addWalletFromWC) return;
    if (wcConnected && wcAddress) {
      setAddWalletFromWC(false);
      setWcAddressForModal(wcAddress); // pass address to modal
      setIsAddWalletOpen(true);
    }
  }, [addWalletFromWC, wcConnected, wcAddress]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [avatarUrl, setAvatarUrl] = useState(() => localStorage.getItem('trader_avatar_url') || '');
  const [isMobile, setIsMobile] = useState(false);

  const displayName = name || userName || 'User Account';
  const displayInitials = avatarInitials || (displayName !== 'User Account' ? displayName.split(' ').map((n: string) => n[0]).join('').toUpperCase().slice(0, 2) : 'UA');

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const handleModalSuccess = () => {
    setIsAddModalOpen(false);
    setRefreshKey(prev => prev + 1);
    cexContext.fetchAccounts().then((accounts) => {
      window.dispatchEvent(new CustomEvent('cex_account_added', { detail: { accounts } }));
    });
  };

  const handleWalletSuccess = () => {
    setIsAddWalletOpen(false);
    setAddWalletFromWC(false);
    cexContext.fetchWalletAccounts();
  };

  // Close dropdown on click outside
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const target = e.target as Node;
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(target) &&
        !(target as Element).closest?.('[data-bottom-sheet]')
      ) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Listen to profile avatar updates in real-time
  useEffect(() => {
    const handleAvatarChange = () => {
      setAvatarUrl(localStorage.getItem('trader_avatar_url') || '');
    };
    window.addEventListener('storage', handleAvatarChange);
    window.addEventListener('avatar_updated', handleAvatarChange);
    return () => {
      window.removeEventListener('storage', handleAvatarChange);
      window.removeEventListener('avatar_updated', handleAvatarChange);
    };
  }, []);

  // Use external refresh key if provided, otherwise use internal
  const effectiveRefreshKey = externalRefreshKey !== undefined ? externalRefreshKey : refreshKey;

  const effectiveSelectedAccount = selectedAccount !== undefined ? selectedAccount : cexContext.selectedAccount;
  const effectiveOnAccountChange = onAccountChange !== undefined ? onAccountChange : ((account: CexAccount) => cexContext.setSelectedAccount(account));

  return (
    <>
      <div className={`h-16 md:h-20 border-b border-neutral-900 flex items-center justify-between px-4 md:px-8 fixed md:sticky top-0 left-0 right-0 bg-black/90 backdrop-blur-md shrink-0 transition-[z-index] pt-[env(safe-area-inset-top)] md:pt-0 ${isMobileOpen ? 'z-[29]' : 'z-50'}`}>
        <div className="flex items-center gap-2 md:gap-4 min-w-0 overflow-hidden flex-1">
          {title && (
            <h1 className="text-base md:text-xl font-bold tracking-tight text-white select-none pl-14 md:pl-0 truncate shrink-0">
              {title}
            </h1>
          )}
          {children}
        </div>
        
        <div className="flex items-center gap-1.5 md:gap-3 shrink-0 ml-2">
          {/* Demo/Live Toggle Switch */}
          {showDemoToggle && onDemoToggle && (
            <div className="bg-[#0B0C10] p-1 rounded-xl flex gap-1 border border-[#16171F] select-none h-8 md:h-10 items-center">
              <button
                onClick={() => onDemoToggle(false)}
                className={`px-2 md:px-3 py-1 rounded-lg text-[9px] md:text-[10px] font-black uppercase tracking-wider transition-all duration-300 h-full flex items-center cursor-pointer ${!isDemoMode ? 'bg-[#22C55E] text-black shadow-md shadow-green-950/20' : 'text-neutral-500 hover:text-neutral-300'}`}
              >
                Live
              </button>
              <button
                onClick={() => onDemoToggle(true)}
                className={`px-2 md:px-3 py-1 rounded-lg text-[9px] md:text-[10px] font-black uppercase tracking-wider transition-all duration-300 h-full flex items-center cursor-pointer ${isDemoMode ? 'bg-[#E50914] text-white shadow-md shadow-red-950/20' : 'text-neutral-500 hover:text-neutral-300'}`}
              >
                Demo
              </button>
            </div>
          )}

          {/* Re-sync Button */}
          {showResync && onResync && (
            <button
              onClick={onResync}
              disabled={isSyncing}
              className="flex items-center justify-center h-8 w-8 md:h-10 md:w-10 bg-[#0B0C10] border border-[#16171F] rounded-xl hover:bg-neutral-900 hover:border-[#E50914]/40 transition-all duration-300 disabled:opacity-50 group cursor-pointer"
              title={isSyncing ? "Syncing..." : "Re-sync Data"}
            >
              <RefreshCw
                size={14}
                className={`${isSyncing ? 'animate-spin text-[#E50914]' : 'text-neutral-400 group-hover:text-white'} transition-colors`}
              />
            </button>
          )}

          {/* CEX Account Switcher — hidden on mobile, visible md+ */}
          {showAccountSwitcher && effectiveOnAccountChange && (
            <div data-tour="topbar-cex" className="hidden md:block">
              <CexAccountSwitcher
                key={effectiveRefreshKey}
                selectedAccount={effectiveSelectedAccount}
                onAccountChange={effectiveOnAccountChange}
                onAddAccount={() => setIsAddModalOpen(true)}
                onAddWallet={() => setIsAddWalletOpen(true)}
                refreshTrigger={effectiveRefreshKey}
              />
            </div>
          )}
          
          <div className="flex items-center gap-2 md:gap-5">
            {/* 1. Language switcher — hidden on mobile */}
            <div className="hidden md:block">
              <LanguageSwitcher />
            </div>
            
            {/* 2. Dark mode — hidden on mobile */}
            <button 
              onClick={toggleTheme}
              title={theme === 'dark' ? t('topbar.switchToLight') : t('topbar.switchToDark')}
              className="hidden md:block text-neutral-500 hover:text-white transition-colors cursor-pointer"
            >
              {theme === 'dark' ? <Moon size={20} /> : <Sun size={20} />}
            </button>

            {/* 3. User avatar only on mobile, full dropdown on desktop */}
            <div className="relative" ref={dropdownRef}>
              <button 
                onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                className="flex items-center gap-2 md:gap-3 p-1.5 hover:bg-neutral-900 border border-transparent hover:border-neutral-800 rounded-2xl transition-all cursor-pointer outline-none select-none text-left"
              >
                {avatarUrl ? (
                  <img src={avatarUrl} alt="Avatar" className="w-8 h-8 md:w-9 md:h-9 rounded-full object-cover border border-[#8CD8FF]/30 shadow-sm" />
                ) : (
                  <div className="w-8 h-8 md:w-9 md:h-9 bg-[#A3E2FF] text-[#0A0A0A] rounded-full flex items-center justify-center font-bold text-xs uppercase tracking-tight shadow-md select-none">
                    {displayInitials}
                  </div>
                )}
                <span className="hidden md:block text-xs font-bold text-neutral-300 pr-1">{displayName}</span>
                <ChevronDown size={14} className={`hidden md:block text-neutral-500 transition-all ${isDropdownOpen ? 'rotate-180 text-white' : ''}`} />
              </button>

              {/* Desktop Dropdown — tetap di dalam relative div */}
              <AnimatePresence>
                {isDropdownOpen && !isMobile && (
                  <motion.div 
                    initial={{ opacity: 0, y: 10, scale: 0.95 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 10, scale: 0.95 }}
                    transition={{ duration: 0.15, ease: 'easeOut' }}
                    className="absolute right-0 mt-2.5 w-56 bg-[#0D0D0D] border border-neutral-800 rounded-2xl shadow-2xl p-1.5 space-y-0.5"
                    style={{ zIndex: 9999 }}
                  >
                    <button 
                      onClick={() => { setIsDropdownOpen(false); navigate('/changelog'); }}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-neutral-400 hover:text-white hover:bg-neutral-900 transition-all text-xs font-bold font-mono tracking-wider uppercase border border-transparent text-left"
                    >
                      <Info size={16} className="text-red-500" />
                      {t('topbar.whatsNew')}
                    </button>
                    <a 
                      href="https://discord.gg/nshaJqNA4X"
                      target="_blank"
                      rel="noopener noreferrer"
                      onClick={() => setIsDropdownOpen(false)}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-neutral-400 hover:text-white hover:bg-neutral-900 transition-all text-xs font-bold font-mono tracking-wider uppercase border border-transparent"
                    >
                      <MessageSquare size={16} className="text-blue-500" />
                      {t('topbar.joinCommunity')}
                    </a>
                    <button 
                      onClick={() => { setIsDropdownOpen(false); setIsSettingsOpen(true); }}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-neutral-400 hover:text-white hover:bg-neutral-900 transition-all text-xs font-bold font-mono tracking-wider uppercase border border-transparent text-left"
                    >
                      <SettingsIcon size={16} className="text-neutral-500" />
                      {t('topbar.settings')}
                    </button>
                    
                    <div className="h-px bg-neutral-900/60 my-1" />
                    
                    <button 
                      onClick={() => { setIsDropdownOpen(false); logout(); }}
                      className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-neutral-400 hover:text-red-500 hover:bg-red-500/5 transition-all text-xs font-bold font-mono tracking-wider uppercase border border-transparent text-left"
                    >
                      <LogOut size={16} className="text-red-500" />
                      {t('topbar.logout')}
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Mobile Bottom Sheet — rendered via portal to document.body */}
            <MobileBottomSheet
              isOpen={isDropdownOpen && isMobile}
              onClose={() => setIsDropdownOpen(false)}
            >
              {/* Handle bar */}
              <div className="w-10 h-1 bg-neutral-700 rounded-full mx-auto mb-4 mt-2" />
              
              {/* User info header */}
              <div className="flex items-center gap-3 px-4 py-3 mb-2">
                {avatarUrl ? (
                  <img src={avatarUrl} alt="Avatar" className="w-10 h-10 rounded-full object-cover border border-[#8CD8FF]/30" />
                ) : (
                  <div className="w-10 h-10 bg-[#A3E2FF] text-[#0A0A0A] rounded-full flex items-center justify-center font-bold text-sm uppercase">
                    {displayInitials}
                  </div>
                )}
                <div>
                  <p className="text-sm font-bold text-white">{displayName}</p>
                  <p className="text-xs text-neutral-500">Account Settings</p>
                </div>
              </div>
              
              <div className="h-px bg-neutral-800 mx-4 mb-2" />
              
              {/* CEX Account Switcher in mobile */}
              {showAccountSwitcher && (
                <>
                  <div className="px-4 py-3">
                    <p className="text-[10px] font-black uppercase tracking-wider text-neutral-500 mb-2">Account</p>
                    <CexAccountSwitcher
                      key={effectiveRefreshKey}
                      selectedAccount={effectiveSelectedAccount}
                      onAccountChange={(account) => {
                        effectiveOnAccountChange(account);
                        setIsDropdownOpen(false);
                      }}
                      onAddAccount={() => {
                        setIsDropdownOpen(false);
                        setIsAddModalOpen(true);
                      }}
                      onAddWallet={() => {
                        setIsDropdownOpen(false);
                        setIsAddWalletOpen(true);
                      }}
                      refreshTrigger={effectiveRefreshKey}
                    />
                  </div>
                  <div className="h-px bg-neutral-800 mx-4 mb-1" />
                </>
              )}
              
              {/* Language */}
              <div className="px-4 py-3 flex items-center justify-between">
                <span className="text-xs font-bold text-neutral-400 uppercase tracking-wider">Language</span>
                <LanguageSwitcher />
              </div>
              
              {/* Dark mode */}
              <div className="px-4 py-3 flex items-center justify-between rounded-xl hover:bg-neutral-900 transition-all mx-2">
                <span className="text-xs font-bold text-neutral-400 uppercase tracking-wider">
                  {theme === 'dark' ? t('topbar.switchToLight') : t('topbar.switchToDark')}
                </span>
                <button onClick={toggleTheme} className="text-neutral-400 hover:text-white transition-colors">
                  {theme === 'dark' ? <Moon size={20} /> : <Sun size={20} />}
                </button>
              </div>
              
              {/* What's New */}
              <button 
                onClick={() => { setIsDropdownOpen(false); navigate('/changelog'); }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-neutral-400 hover:text-white hover:bg-neutral-900 transition-all text-xs font-bold uppercase tracking-wider"
              >
                <Info size={16} className="text-red-500" />
                {t('topbar.whatsNew')}
              </button>
              
              {/* Join Community */}
              <a 
                href="https://discord.gg/nshaJqNA4X"
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => setIsDropdownOpen(false)}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-neutral-400 hover:text-white hover:bg-neutral-900 transition-all text-xs font-bold uppercase tracking-wider"
              >
                <MessageSquare size={16} className="text-blue-500" />
                {t('topbar.joinCommunity')}
              </a>
              
              {/* Settings */}
              <button 
                onClick={() => { setIsDropdownOpen(false); setIsSettingsOpen(true); }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-neutral-400 hover:text-white hover:bg-neutral-900 transition-all text-xs font-bold uppercase tracking-wider text-left"
              >
                <SettingsIcon size={16} className="text-neutral-500" />
                {t('topbar.settings')}
              </button>
              
              <div className="h-px bg-neutral-800 mx-4 my-1" />
              
              {/* Logout */}
              <button 
                onClick={() => { setIsDropdownOpen(false); logout(); }}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-xl text-neutral-400 hover:text-red-500 hover:bg-red-500/5 transition-all text-xs font-bold uppercase tracking-wider text-left"
              >
                <LogOut size={16} className="text-red-500" />
                {t('topbar.logout')}
              </button>
            </MobileBottomSheet>
          </div>

        </div>
      </div>

      {/* Add CEX Account Modal */}
      <AddCexAccountModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSuccess={handleModalSuccess}
      />

      {/* Add Wallet Modal */}
      <AddWalletModal
        isOpen={isAddWalletOpen}
        onClose={() => {
          setIsAddWalletOpen(false);
          setAddWalletFromWC(false);
          setWcAddressForModal(undefined);
        }}
        onWalletConnectInitiated={() => {
          setIsAddWalletOpen(false);
          setAddWalletFromWC(true);
          setWcAddressForModal(undefined);
        }}
        onSuccess={handleWalletSuccess}
        prefilledAddress={wcAddressForModal}
        prefilledType={wcAddressForModal ? 'evm' : undefined}
      />

      {/* Settings Modal */}
      <SettingsModal 
        isOpen={isSettingsOpen}
        onClose={() => setIsSettingsOpen(false)}
      />
    </>
  );
};

export default TopBar;

